// Canonical field-definition schema, ported from the Ruby wrappers in
// app/services/dynamic_forms/{definition,field}.rb and
// app/services/dynamic_forms/fields/*.rb. Keep these in sync with that source
// of truth — the importer (alpaca_importer.rb) emits exactly these shapes.

// `FieldState` lives in introspection.ts (which imports types from here). This
// is a type-only import — erased at runtime, so there is no module cycle.
import type { FieldState } from './introspection'

export const CURRENT_SCHEMA_VERSION = 1

/** The 14 field types declared in DynamicForms::Field::TYPES. */
export const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'boolean',
  'radio',
  'select',
  'multi_select',
  'multi_checkbox',
  'scalar_list',
  'upload',
  'group',
  'markup',
  'hidden',
] as const

export type FieldType = (typeof FIELD_TYPES)[number]

export type HelpPosition = 'above' | 'below'

/**
 * Inline enum choice. `value` keeps its JSON-native type (string, boolean,
 * number, …) — do not coerce to string. `active: false` options are filtered
 * out of the UI unless the current value matches one (preserving legacy data).
 */
export interface Option {
  value: unknown
  label: string
  active?: boolean
}

// --- Visibility / dependency rules -----------------------------------------

export const VISIBILITY_OPERATORS = [
  'present',
  'equals',
  'not_equals',
  'in',
  'not_in',
] as const

export type VisibilityOperator = (typeof VISIBILITY_OPERATORS)[number]

/**
 * Conditional-show rule. AND semantics across a field's rules (no OR in v1).
 * `source_field_key` resolves against the field's parent group, walking
 * outward to the form root. `value` is unused for the `present` operator and
 * is an array for `in` / `not_in`. Against an array-valued source, an equality
 * test passes if any selected value matches. A rule only counts when the field
 * it reads is itself visible; a statically `hidden` source is exempt.
 */
export interface VisibilityRule {
  source_field_key: string
  operator: VisibilityOperator
  value?: unknown
}

/** Raw Alpaca data-source descriptor passed through by the importer. */
export type DataSourceDescriptor = Record<string, unknown>

/**
 * Config keys present on every field type (the importer hand-offs documented
 * in field.rb) plus an open index signature so unknown keys round-trip rather
 * than break the renderer (forward-compat policy).
 */
export interface CommonConfig {
  legacy_field_class?: string
  widget?: string
  format?: string
  data_source_raw?: DataSourceDescriptor
  /** Author-supplied captions (label / description / help_text) are HTML and
   * are injected after sanitization against LABEL_ALLOWED_TAGS. Set by the
   * Alpaca importer when it detects markup in the source `title`. */
  html_labels?: boolean
  [key: string]: unknown
}

// --- Per-type config shapes -------------------------------------------------

export interface TextConfig extends CommonConfig {
  min_length?: number
  max_length?: number
  pattern?: string
  placeholder?: string
}

export interface TextareaConfig extends CommonConfig {
  rows?: number
  cols?: number
  min_length?: number
  max_length?: number
  placeholder?: string
  disallow_empty_spaces?: boolean
  disallow_only_empty_spaces?: boolean
  /** Per-field rich text opt-in; requires a host-supplied `richTextAdapter`. */
  rich_text?: boolean
}

export interface NumberConfig extends CommonConfig {
  integer?: boolean
  min?: number
  max?: number
  step?: number
  placeholder?: string
}

export type DateVariant = 'date' | 'datetime'

export interface DateConfig extends CommonConfig {
  variant?: DateVariant
  min?: string
  max?: string
  default_offset?: number
}

export type BooleanRenderAs = 'checkbox' | 'buttons'

export interface BooleanConfig extends CommonConfig {
  render_as?: BooleanRenderAs
}

/** Shared config for radio / select / multi_select / multi_checkbox. */
export interface EnumConfig extends CommonConfig {
  render_as?: string
  hide_none?: boolean
  none_label?: string
  placeholder?: string
  /**
   * Force the native `<select>` / `<select multiple>` for this field even when
   * the host wired an enhanced-select adapter (renderer-only hint).
   */
  plain_select?: boolean
}

export interface MultiConfig extends EnumConfig {
  min_items?: number
  max_items?: number
}

export type ScalarItemType = 'string' | 'integer' | 'number' | 'boolean'

export interface ScalarListConfig extends CommonConfig {
  item_type?: ScalarItemType
  min_items?: number
  max_items?: number
  placeholder?: string
}

export interface UploadConfig extends CommonConfig {
  multiple?: boolean
  accepted_types?: string[]
  max_size?: number
  min_items?: number
  max_items?: number
}

export type GroupLayout = 'stacked' | 'table'

export type GroupStyle = 'well' | 'alert_info' | 'alert_warning' | 'alert_danger' | 'alert_success'

/** Bootstrap classes each `GroupStyle` token expands to. The single place to
 *  change if the underlying class names move. `CommonConfig` carries an open
 *  index signature, so an unknown `style` value can reach the renderer: always
 *  look the token up here, never interpolate it into a class string. */
export const GROUP_STYLE_CLASSES: Record<GroupStyle, string> = {
  well: 'well',
  alert_info: 'alert alert-info',
  alert_warning: 'alert alert-warning',
  alert_danger: 'alert alert-danger',
  alert_success: 'alert alert-success',
}

export interface GroupConfig extends CommonConfig {
  layout?: GroupLayout
  style?: GroupStyle
  collapsible?: boolean
  legend_style?: string
  actionbar_labels?: Record<string, string>
  /** Row reordering on a repeatable group; defaults to enabled. Alpaca forms that
   *  disabled the up/down actions import as `false`. */
  reorderable?: boolean
  repeat_min?: number
  repeat_max?: number
}

export interface MarkupConfig extends CommonConfig {
  body?: string
  style?: string
  level?: string
}

// --- Field shapes -----------------------------------------------------------

/** Attributes shared by every field (DynamicForms::Field base attributes). */
export interface FieldBase {
  key: string
  label?: string
  description?: string
  help_text?: string
  help_position?: HelpPosition
  required?: boolean
  hidden?: boolean
  disabled?: boolean
  readonly?: boolean
  display_only?: boolean
  default_value?: unknown
  visibility?: VisibilityRule[]
}

export interface TextField extends FieldBase {
  type: 'text'
  config?: TextConfig
}

export interface TextareaField extends FieldBase {
  type: 'textarea'
  config?: TextareaConfig
}

export interface NumberField extends FieldBase {
  type: 'number'
  config?: NumberConfig
}

export interface DateField extends FieldBase {
  type: 'date'
  config?: DateConfig
}

export interface BooleanField extends FieldBase {
  type: 'boolean'
  config?: BooleanConfig
}

export interface RadioField extends FieldBase {
  type: 'radio'
  config?: EnumConfig
  options?: Option[]
}

export interface SelectField extends FieldBase {
  type: 'select'
  config?: EnumConfig
  options?: Option[]
}

export interface MultiSelectField extends FieldBase {
  type: 'multi_select'
  config?: MultiConfig
  options?: Option[]
}

export interface MultiCheckboxField extends FieldBase {
  type: 'multi_checkbox'
  config?: MultiConfig
  options?: Option[]
}

export interface ScalarListField extends FieldBase {
  type: 'scalar_list'
  config?: ScalarListConfig
}

export interface UploadField extends FieldBase {
  type: 'upload'
  config?: UploadConfig
}

export interface GroupField extends FieldBase {
  type: 'group'
  config?: GroupConfig
  /** Nested fields, from the raw field's top-level `children` array. */
  children?: DynamicFormField[]
}

export interface MarkupField extends FieldBase {
  type: 'markup'
  config?: MarkupConfig
}

export interface HiddenField extends FieldBase {
  type: 'hidden'
  config?: CommonConfig
}

/** Discriminated union of all known field types. */
export type DynamicFormField =
  | TextField
  | TextareaField
  | NumberField
  | DateField
  | BooleanField
  | RadioField
  | SelectField
  | MultiSelectField
  | MultiCheckboxField
  | ScalarListField
  | UploadField
  | GroupField
  | MarkupField
  | HiddenField

/**
 * Loose shape for a field as it arrives from JSON, before it is known to match
 * a supported type. Used at the parse boundary and by the renderer's
 * unknown-type fallback (`UnsupportedField`).
 */
export interface RawField {
  key?: string
  type?: string
  children?: RawField[]
  [key: string]: unknown
}

export interface DynamicFormDefinition {
  schema_version: number
  fields: DynamicFormField[]
}

// --- Values -----------------------------------------------------------------

/**
 * A form's value tree. Keyed by field key at each level. Group values are
 * nested objects; repeatable-group values are arrays of objects (each carrying
 * an internal `__key`). Leaf values keep their JSON-native type.
 */
export type DynamicFormValue = Record<string, unknown>

/** Internal, per-row stable key for repeatable-group reconciliation. */
export const ROW_KEY = '__key'

// --- Uploads ----------------------------------------------------------------

/** Matches Pg::TempAttachment#to_jq_upload. */
export interface UploadFileNode {
  id: string
  name: string
  size: number
  url: string
  thumbnailUrl: string | null
  deleteUrl: string
  deleteType: 'DELETE'
  couch_id?: string
}

/** Per-upload context handed to an {@link UploadAdapter}. */
export interface UploadContext {
  /** Progress callback for the in-flight upload (bytes loaded / total). */
  onProgress?: (loaded: number, total: number) => void
  /** Aborted when the upload is cancelled or the field unmounts. */
  signal: AbortSignal
}

/**
 * Host-supplied function that persists one selected `File` and resolves to the
 * stored node shape. The renderer owns the AbortController; the adapter must
 * wire `context.signal` to its own cancellation (e.g. `xhr.abort()`).
 */
export type UploadAdapter = (
  file: File,
  field: DynamicFormField,
  context: UploadContext,
) => Promise<UploadFileNode>

// --- Validation -------------------------------------------------------------

export interface ValidationError {
  /** Stable, machine-readable code (e.g. "required", "min_length"). */
  code: string
  /** Human-readable message. Not coupled to ActiveModel wording. */
  message: string
}

/** Errors keyed by field path (e.g. "address.zipcode", "contacts[0].email"). */
export type ValidationErrors = Record<string, ValidationError[]>

// --- Imperative handle ------------------------------------------------------

export interface DynamicFormHandle {
  getValue(): DynamicFormValue
  setValue(value: DynamicFormValue): void
  setFieldValue(path: string, value: unknown): void
  /** Full validation sweep that ALSO displays the error UI (submit gate). */
  validate(): ValidationErrors
  focusField(path: string): void
  reset(): void

  // --- Read-only introspection (live off the current value, no side effects) ---

  /** Every keyed field, any visibility. */
  getFields(): FieldState[]
  /** Only the fields that would render (`getFields().filter(visible)`). */
  getVisibleFields(): FieldState[]
  /** Value at a single path (e.g. "address.zip", "contacts[0].email"). */
  getFieldValue(path: string): unknown
  /** Full validation sweep WITHOUT touching the error UI (silent read). */
  getValidation(): ValidationErrors
  /** One field's errors, silent — `[]` when valid. */
  getFieldValidation(path: string): ValidationError[]
}

// --- Type guards ------------------------------------------------------------

export function isGroupField(field: DynamicFormField): field is GroupField {
  return field.type === 'group'
}

/** A group is repeatable when it declares repeat_min or repeat_max. */
export function isRepeatableGroup(field: DynamicFormField): boolean {
  if (field.type !== 'group') return false
  const config = field.config ?? {}
  return 'repeat_min' in config || 'repeat_max' in config
}

/** A `boolean` drawn as a Yes/No pair rather than a checkbox. The two disagree
 *  on what `false` means — an unticked checkbox is not yet answered, whereas
 *  "No" is an answer — which drives both seeding and the required check. */
export function isBooleanToggle(field: DynamicFormField): boolean {
  if (field.type !== 'boolean') return false
  return (field.config as BooleanConfig | undefined)?.render_as === 'buttons'
}

/** An imported Alpaca alert box, which saved `{}` while on screen — renderers
 *  and config rules test for that key, so it keeps saving one. */
export function isAlertMarkup(field: DynamicFormField): boolean {
  if (field.type !== 'markup' || field.default_value !== undefined) return false
  const config = field.config ?? {}
  return !config.widget && /\balert\b/.test(config.legacy_field_class ?? '')
}
